import { type MetaDescriptor } from 'react-router'

export const ORGANIZATION_LD = {
	'@context': 'https://schema.org',
	'@type': 'Organization',
	name: 'Syrcha',
	url: 'https://syrcha.com',
	logo: 'https://syrcha.com/favicon.svg',
	description:
		'Automated privacy compliance monitoring for GDPR, CCPA, and AI Act. Scan your website for compliance issues and get actionable recommendations.',
	sameAs: [
		'https://github.com/syrcha',
		'https://x.com/getsyrcha',
		'https://bsky.app/profile/syrcha.bsky.social',
		'https://www.instagram.com/getsyrcha',
	],
}

export const SOFTWARE_APPLICATION_LD = {
	'@context': 'https://schema.org',
	'@type': 'SoftwareApplication',
	name: 'Syrcha',
	applicationCategory: 'SecurityApplication',
	operatingSystem: 'Web',
	url: 'https://syrcha.com',
	description:
		'Automated privacy compliance scanner for GDPR, CCPA, WCAG and the EU AI Act. Real browser checks, signed audit-ready reports, and actionable fixes.',
	offers: {
		'@type': 'AggregateOffer',
		priceCurrency: 'USD',
		lowPrice: '29',
		highPrice: '249',
		offerCount: '3',
	},
}

type RequestInfo = { origin: string; path: string }

type RootMatch = { id: string; data: unknown }

function isRootMatch(match: unknown): match is RootMatch {
	return (
		typeof match === 'object' &&
		match !== null &&
		'id' in match &&
		(match as { id: unknown }).id === 'root'
	)
}

/**
 * Pull the request origin/path out of the root loader's `requestInfo`, which is
 * exposed on the `root` match. Meta functions don't have access to the
 * `request`, so this is how child routes learn the absolute URL to build
 * canonical and Open Graph tags.
 *
 * `matches` is typed as `readonly unknown[]` so this accepts the per-route
 * readonly match tuple React Router generates for every route, without each
 * caller having to line up its exact generated type.
 */
function getRequestInfo(matches: readonly unknown[]): RequestInfo | null {
	const root = matches.find(isRootMatch)
	const data = root?.data as { requestInfo?: RequestInfo } | undefined
	return data?.requestInfo ?? null
}

/**
 * Build a complete, consistent set of SEO meta tags - title, description,
 * canonical, Open Graph and Twitter Card - for a page.
 *
 * In React Router a child route's `meta` export *replaces* the parent's rather
 * than merging, so every public route must emit its own full tag set or it
 * silently loses the canonical/OG/Twitter tags defined on the root. Routing all
 * marketing pages through this helper keeps them in sync and makes adding a new
 * page a one-liner.
 */
export function buildMeta({
	title,
	description,
	matches,
	image = '/og-image.png',
}: {
	title: string
	description: string
	matches: readonly unknown[]
	/** Path (or absolute URL) to the social share image. */
	image?: string
}): MetaDescriptor[] {
	const requestInfo = getRequestInfo(matches)
	const canonicalUrl = requestInfo
		? `${requestInfo.origin}${requestInfo.path}`
		: null
	const imageUrl = requestInfo
		? image.startsWith('http')
			? image
			: `${requestInfo.origin}${image}`
		: null

	return [
		{ title },
		{ name: 'description', content: description },
		// Open Graph
		{ property: 'og:type', content: 'website' },
		{ property: 'og:site_name', content: 'Syrcha' },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		...(canonicalUrl ? [{ property: 'og:url', content: canonicalUrl }] : []),
		...(imageUrl
			? [
					{ property: 'og:image', content: imageUrl },
					{ property: 'og:image:width', content: '1200' },
					{ property: 'og:image:height', content: '630' },
					{ property: 'og:image:alt', content: title },
				]
			: []),
		// Twitter Card
		{ name: 'twitter:card', content: 'summary_large_image' },
		{ name: 'twitter:title', content: title },
		{ name: 'twitter:description', content: description },
		...(imageUrl ? [{ name: 'twitter:image', content: imageUrl }] : []),
		// Canonical
		...(canonicalUrl
			? [{ tagName: 'link' as const, rel: 'canonical', href: canonicalUrl }]
			: []),
	]
}

/**
 * Meta for pages that must never appear in search results (auth screens,
 * one-time-code flows). These pages are also absent from sitemap.xml.
 */
export function noIndexMeta(title: string): MetaDescriptor[] {
	return [{ title }, { name: 'robots', content: 'noindex, nofollow' }]
}
